import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Groups as GroupsIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useLanguage } from '../contexts/LanguageContext';

const tandaSchema = z.object({
  name: z.string().min(1, 'Tanda name is required').max(100, 'Name is too long'),
  description: z.string().max(500, 'Description is too long').optional(),
  total_amount: z.number().min(10, 'Total amount must be at least $10').max(100000, 'Amount is too large'),
  member_count: z.number().min(2, 'At least 2 members required').max(50, 'Maximum 50 members allowed'),
  contribution_amount: z.number().min(1, 'Contribution must be at least $1'),
  frequency: z.enum(['weekly', 'biweekly', 'monthly']),
  start_date: z.date().min(new Date(), 'Start date must be in the future'),
}).refine((data) => data.total_amount === data.contribution_amount * data.member_count, {
  message: 'Total amount must equal contribution amount x member count',
  path: ['total_amount'],
});

type TandaData = z.infer<typeof tandaSchema>;

const CreateTanda: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const steps = [t('createTanda.step1'), t('createTanda.step2'), t('createTanda.step3'), t('createTanda.step4')];

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm<TandaData>({
    resolver: zodResolver(tandaSchema),
    defaultValues: {
      name: '',
      description: '',
      total_amount: 1000,
      member_count: 10,
      contribution_amount: 100,
      frequency: 'monthly',
      start_date: new Date(new Date().setDate(new Date().getDate() + 7)), // One week from now
    },
  });

  const watchAllFields = watch();

  // Calculate derived values
  const calculateTotalAmount = () => {
    return watchAllFields.contribution_amount * watchAllFields.member_count;
  };

  const calculateDuration = () => {
    const { member_count, frequency } = watchAllFields;
    switch (frequency) {
      case 'weekly':
        return `${member_count} ${t('createTanda.weeks')}`;
      case 'biweekly':
        return `${member_count * 2} ${t('createTanda.weeks')}`;
      case 'monthly':
        return `${member_count} ${t('tandaDetail.months') || 'months'}`;
      default:
        return '';
    }
  };

  const handleNext = async () => {
    const fieldsToValidate = getFieldsForStep(activeStep);
    const isValid = await trigger(fieldsToValidate);

    if (isValid) {
      setActiveStep((prevStep) => prevStep + 1);
      setError(null);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError(null);
  };

  const getFieldsForStep = (step: number): (keyof TandaData)[] => {
    switch (step) {
      case 0:
        return ['name', 'description'];
      case 1:
        return ['total_amount', 'member_count', 'contribution_amount'];
      case 2:
        return ['frequency', 'start_date'];
      default:
        return [];
    }
  };

  const onSubmit = async (data: TandaData) => {
    try {
      setLoading(true);
      setError(null);

      // Ensure total amount matches calculation
      const calculatedTotal = data.contribution_amount * data.member_count;
      const finalData = {
        ...data,
        total_amount: calculatedTotal,
        start_date: data.start_date.toISOString(),
      };

      await axios.post('/tandas', finalData);
      enqueueSnackbar('Tanda created successfully!', { variant: 'success' });
      navigate('/tandas');
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to create tanda');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('createTanda.tandaName')}
                    variant="outlined"
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <GroupsIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('createTanda.description')}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={4}
                    error={!!errors.description}
                    helperText={errors.description?.message || t('createTanda.describeHelp')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DescriptionIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Controller
                name="member_count"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('createTanda.numberOfMembers')}
                    type="number"
                    variant="outlined"
                    fullWidth
                    error={!!errors.member_count}
                    helperText={errors.member_count?.message}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 0;
                      field.onChange(value);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <GroupsIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="contribution_amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('createTanda.contributionAmount')}
                    type="number"
                    variant="outlined"
                    fullWidth
                    error={!!errors.contribution_amount}
                    helperText={errors.contribution_amount?.message}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      field.onChange(value);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MoneyIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t('createTanda.totalAmount')}
                type="number"
                variant="outlined"
                fullWidth
                value={calculateTotalAmount()}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MoneyIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {t('createTanda.calculatedAuto')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  {t('createTanda.eachMemberContributes')} ${watchAllFields.contribution_amount} MXN {t('createTanda.perPeriod')}{' '}
                  {t('createTanda.totalPotOf')} ${calculateTotalAmount()} MXN {t('createTanda.distributedAmong')} {watchAllFields.member_count} {t('createTanda.membersWord')}
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Controller
                name="frequency"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.frequency}>
                    <InputLabel>{t('createTanda.paymentFrequency')}</InputLabel>
                    <Select {...field} label={t('createTanda.paymentFrequency')}>
                      <MenuItem value="weekly">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ScheduleIcon sx={{ mr: 1 }} />
                          {t('common.weekly')}
                        </Box>
                      </MenuItem>
                      <MenuItem value="biweekly">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ScheduleIcon sx={{ mr: 1 }} />
                          {t('common.biweekly')}
                        </Box>
                      </MenuItem>
                      <MenuItem value="monthly">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ScheduleIcon sx={{ mr: 1 }} />
                          {t('common.monthly')}
                        </Box>
                      </MenuItem>
                    </Select>
                    {errors.frequency && (
                      <Typography variant="caption" color="error">
                        {errors.frequency.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Controller
                  name="start_date"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label={t('createTanda.startDate')}
                      value={field.value}
                      onChange={field.onChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.start_date,
                          helperText: errors.start_date?.message,
                          InputProps: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarIcon color="action" />
                              </InputAdornment>
                            ),
                          },
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  {t('createTanda.tandaWillRun')} {calculateDuration()}, {t('createTanda.withPeriods')} {watchAllFields.member_count} {t('createTanda.paymentPeriods')}{' '}
                  {t('createTanda.membersWillPay')} {watchAllFields.frequency === 'biweekly' ? `2 ${t('createTanda.weeks')}` : watchAllFields.frequency === 'weekly' ? t('common.weekly').toLowerCase() : t('common.monthly').toLowerCase()}.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('createTanda.review')}
            </Typography>

            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      {watchAllFields.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {watchAllFields.description || 'No description provided.'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('createTanda.membersLabel')}
                    </Typography>
                    <Typography variant="body1">
                      {watchAllFields.member_count}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('createTanda.contributionLabel')}
                    </Typography>
                    <Typography variant="body1">
                      ${watchAllFields.contribution_amount} MXN
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('createTanda.totalPot')}
                    </Typography>
                    <Typography variant="body1" color="primary">
                      ${calculateTotalAmount()} MXN
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('createTanda.frequencyLabel')}
                    </Typography>
                    <Typography variant="body1">
                      {watchAllFields.frequency.charAt(0).toUpperCase() + watchAllFields.frequency.slice(1)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('createTanda.startDateLabel')}
                    </Typography>
                    <Typography variant="body1">
                      {watchAllFields.start_date.toLocaleDateString()}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('createTanda.durationLabel')}
                    </Typography>
                    <Typography variant="body1">
                      {calculateDuration()}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Alert severity="warning">
              <Typography variant="body2">
                <strong>{t('createTanda.importantNote')}</strong> {t('createTanda.importantText')}
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md">
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tandas')}
          sx={{ mb: 2 }}
        >
          {t('createTanda.backToTandas')}
        </Button>

        <Typography variant="h4" component="h1" gutterBottom>
          {t('createTanda.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('createTanda.subtitle')}
        </Typography>
      </Box>

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Form */}
      <Paper sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {renderStepContent(activeStep)}

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0 || loading}
            >
              {t('createTanda.back')}
            </Button>

            {activeStep === steps.length - 1 ? (
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? t('createTanda.creating') : t('createTanda.create')}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
              >
                {t('createTanda.next')}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Help Card */}
      <Paper sx={{ p: 3, mt: 3, backgroundColor: 'info.light', color: 'info.contrastText' }}>
        <Typography variant="h6" gutterBottom>
          {t('createTanda.tipsTitle')}
        </Typography>
        <Typography variant="body2">
          {t('createTanda.tip1')}
          <br />
          {t('createTanda.tip2')}
          <br />
          {t('createTanda.tip3')}
          <br />
          {t('createTanda.tip4')}
          <br />
          {t('createTanda.tip5')}
        </Typography>
      </Paper>
    </Container>
  );
};

export default CreateTanda;
